export const en = {
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  pageTitle: 'DOMINION | Risk Profile',
  title: 'Risk Profile Report',
  profileTitle: 'Your Risk Profile:',
  allocationTitle: 'Recommended Asset Allocation',
  recommendationsTitle: 'Investment Recommendations:',
  recommendationText: 'of your portfolio',
  footer: 'This is a system-generated document. For internal use only.',
  website: 'www.dominion-cs.com',
  signature: {
    title: 'Signature and Acceptance',
    clientName: 'Client Name:',
    signature: 'Signature:',
    date: 'Date:',
    advisorName: 'Advisor Name:',
  },
  header: {
    poweredBy: "Powered by DOMINION"
  },
  welcome: {
    title: "Welcome to the Risk Profile",
    description: "This questionnaire will help you determine your risk profile as an investor and provide you with a personalised recommendation on how to distribute your investments.",
    whatWeEvaluate: "What will we evaluate?",
    items: [
      "Your investment time horizon",
      "Your experience in financial markets",
      "Your tolerance to volatility",
      "Your investment objectives"
    ],
    duration: "The questionnaire takes approximately 3-5 minutes to complete.",
    keyboardHint: "Press {enter} to start"
  },
  questions: [
    {
      id: 'q1',
      question: 'What is your investment time horizon?',
      options: [
        { value: '10', label: '1-3 years', description: 'Medium-short term' },
        { value: '15', label: '3-5 years', description: 'Medium term' },
        { value: '20', label: '5-10 years', description: 'Medium-long term' },
        { value: '25', label: 'More than 10 years', description: 'Long term, no rush' },
      ]
    },
    {
      id: 'q2',
      question: 'What is your investment experience?',
      options: [
        { value: '0', label: 'None', description: 'I\'m new to this' },
        { value: '5', label: 'Basic', description: 'Some simple investments' },
        { value: '10', label: 'Intermediate', description: 'Several years investing' },
        { value: '15', label: 'Advanced', description: 'Extensive market experience' },
        { value: '20', label: 'Expert', description: 'Professional or highly experienced' },
      ]
    },
    {
      id: 'q3',
      question: 'If your investment dropped 20% in one month, what would you do?',
      options: [
        { value: '0', label: 'Sell everything immediately', description: 'I can\'t bear losses' },
        { value: '5', label: 'Sell most of it', description: 'I\'d get very nervous' },
        { value: '10', label: 'Wait and see', description: 'I\'d analyse the situation' },
        { value: '15', label: 'Hold my position', description: 'I trust my strategy' },
        { value: '20', label: 'Buy more', description: 'It\'s an opportunity' },
      ]
    },
    {
      id: 'q4',
      question: 'What percentage of your savings will you invest?',
      options: [
        { value: '20', label: 'Less than 10%', description: 'Just a small portion' },
        { value: '15', label: '10-25%', description: 'A moderate portion' },
        { value: '10', label: '25-50%', description: 'A significant portion' },
        { value: '5', label: '50-75%', description: 'Most of it' },
        { value: '0', label: 'More than 75%', description: 'Nearly all my savings' },
      ]
    },
    {
      id: 'q5',
      question: 'What is your main investment objective?',
      options: [
        { value: '0', label: 'Preserve capital', description: 'I don\'t want to lose money' },
        { value: '5', label: 'Generate stable income', description: 'Sustained growth above inflation' },
        { value: '10', label: 'Moderate growth', description: 'Balance between security and returns' },
        { value: '15', label: 'Significant growth', description: 'Maximise gains with controlled risk' },
        { value: '20', label: 'Maximum returns', description: 'I seek the highest returns possible' },
      ]
    },
    {
      id: 'q6',
      question: 'How do you react to market volatility?',
      options: [
        { value: '0', label: 'It stresses me greatly', description: 'I lose sleep over it' },
        { value: '5', label: 'It worries me quite a bit', description: 'I constantly check my investments' },
        { value: '10', label: 'It concerns me slightly', description: 'I monitor but don\'t obsess' },
        { value: '15', label: 'I accept it as normal', description: 'It\'s part of the process' },
        { value: '20', label: 'I see it as opportunity', description: 'It allows me to find better prices' },
      ]
    },
  ],
  questionCard: {
    keyboardHint: "Press {numbers} to select or {enter} to continue"
  },
  riskProfiles: {
    conservative: {
      profile: 'Conservative',
      description: 'You prioritise the security and stability of your capital. You prefer to minimise risks even if this means lower potential returns.',
      scoreRange: '0-20',
      ranges: [
        { category: 'Fixed Income', min: 70, max: 100, color: '#5955FF' },
        { category: 'Equities', min: 0, max: 30, color: '#90d977' },
        { category: 'Alternatives', min: 0, max: 10, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 0, color: '#FF6B6B' },
      ],
      funds: {
        'Fixed Income': [
          { name: 'DCS Global Bonds', composition: '100% Fixed Income' },
          { name: 'DCS Cash', composition: '100% Fixed Income' },
          { name: 'DCS G10 Macro Rates', composition: '100% Fixed Income' },
        ],
        'Equities': [
          { name: 'DCS Cautious', composition: '20% Equities + 80% Fixed Income' },
          { name: 'DCS Balanced', composition: '60% Equities + 40% Fixed Income' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Equities + 40% Fixed Income' },
        ],
        'Alternatives': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternatives' },
        ],
      }
    },
    moderateConservative: {
      profile: 'Moderate Conservative',
      description: 'You seek a balance tilted towards security, but are willing to take on some risk to obtain better returns.',
      scoreRange: '21-40',
      ranges: [
        { category: 'Fixed Income', min: 50, max: 70, color: '#5955FF' },
        { category: 'Equities', min: 20, max: 50, color: '#90d977' },
        { category: 'Alternatives', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 5, color: '#FF6B6B' },
      ],
      funds: {
        'Fixed Income': [
          { name: 'DCS Global Bonds', composition: '100% Fixed Income' },
          { name: 'DCS Cash', composition: '100% Fixed Income' },
          { name: 'DCS G10 Macro Rates', composition: '100% Fixed Income' },
        ],
        'Equities': [
          { name: 'DCS Cautious', composition: '20% Equities + 80% Fixed Income' },
          { name: 'DCS Balanced', composition: '60% Equities + 40% Fixed Income' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Equities + 40% Fixed Income' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Equities' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Equities' },
        ],
        'Alternatives': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternatives' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderate: {
      profile: 'Moderate',
      description: 'You seek a balanced mix between risk and return. You understand that volatility is part of achieving better long-term returns.',
      scoreRange: '41-60',
      ranges: [
        { category: 'Fixed Income', min: 30, max: 50, color: '#5955FF' },
        { category: 'Equities', min: 40, max: 80, color: '#90d977' },
        { category: 'Alternatives', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 10, color: '#FF6B6B' },
      ],
      funds: {
        'Fixed Income': [
          { name: 'DCS Global Bonds', composition: '100% Fixed Income' },
          { name: 'DCS Cash', composition: '100% Fixed Income' },
          { name: 'DCS G10 Macro Rates', composition: '100% Fixed Income' },
        ],
        'Equities': [
          { name: 'DCS Balanced', composition: '60% Equities + 40% Fixed Income' },
          { name: 'DCS Aggressive', composition: '80% Equities + 20% Fixed Income' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Equities + 40% Fixed Income' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Equities' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Equities' },
          { name: 'DCS Emerging Market Equities', composition: '100% Equities' },
          { name: 'DCS New Technologies', composition: '100% Equities' },
          { name: 'DCS Global Growth', composition: '100% Equities' },
          { name: 'DCS Managed Fund', composition: '100% Equities' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Equities' },
          { name: 'Nasdaq Tracker fund', composition: '100% Equities' },
        ],
        'Alternatives': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternatives' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderateAggressive: {
      profile: 'Moderate Aggressive',
      description: 'You are willing to take on significant risks in pursuit of higher returns. You tolerate volatility and have a long-term investment horizon.',
      scoreRange: '61-80',
      ranges: [
        { category: 'Fixed Income', min: 10, max: 30, color: '#5955FF' },
        { category: 'Equities', min: 60, max: 90, color: '#90d977' },
        { category: 'Alternatives', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 15, color: '#FF6B6B' },
      ],
      funds: {
        'Fixed Income': [
          { name: 'DCS Global Bonds', composition: '100% Fixed Income' },
          { name: 'DCS G10 Macro Rates', composition: '100% Fixed Income' },
        ],
        'Equities': [
          { name: 'DCS Aggressive', composition: '80% Equities + 20% Fixed Income' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Equities' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Equities' },
          { name: 'DCS Emerging Market Equities', composition: '100% Equities' },
          { name: 'DCS New Technologies', composition: '100% Equities' },
          { name: 'DCS Global Growth', composition: '100% Equities' },
          { name: 'DCS Managed Fund', composition: '100% Equities' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Equities' },
          { name: 'Nasdaq Tracker fund', composition: '100% Equities' },
        ],
        'Alternatives': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternatives' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    aggressive: {
      profile: 'Aggressive',
      description: 'You seek to maximise your returns and are willing to take on high levels of risk. You have investment experience and a long time horizon.',
      scoreRange: '81-120',
      ranges: [
        { category: 'Fixed Income', min: 0, max: 10, color: '#5955FF' },
        { category: 'Equities', min: 70, max: 100, color: '#90d977' },
        { category: 'Alternatives', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 20, color: '#FF6B6B' },
      ],
      funds: {
        'Fixed Income': [
          { name: 'DCS Global Bonds', composition: '100% Fixed Income' },
        ],
        'Equities': [
          { name: 'DCS Aggressive', composition: '80% Equities + 20% Fixed Income' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Equities' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Equities' },
          { name: 'DCS Emerging Market Equities', composition: '100% Equities' },
          { name: 'DCS New Technologies', composition: '100% Equities' },
          { name: 'DCS Global Growth', composition: '100% Equities' },
          { name: 'DCS Managed Fund', composition: '100% Equities' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Equities' },
          { name: 'Nasdaq Tracker fund', composition: '100% Equities' },
        ],
        'Alternatives': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternatives' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    }
  },
  results: {
    title: "Your risk profile:",
    chartTitle: "Recommended Asset Allocation",
    recommendationsTitle: "Recommended Ranges",
    fundsTitle: "Suggested Funds",
    recommendationText: "of your portfolio",
    rangeText: "Range:",
    rangeLabel: "Range",
    totalAllocation: "Total Portfolio Allocation",
    noteTitle: "Note:",
    noteText: "This profile is for guidance only. We recommend consulting with a financial adviser before making investment decisions.",
    downloadPDF: "Download PDF",
    restartTest: "Take Test Again",
    preparingPDF: "Preparing your PDF...",
    pleaseWait: "Please wait a moment"
  },
  clientInfoModal: {
    title: "Client Information",
    clientNameLabel: "Client Full Name",
    clientNamePlaceholder: "John Smith",
    advisorNameLabel: "Advisor Full Name",
    advisorNamePlaceholder: "Jane Doe",
    cancelButton: "Cancel",
    confirmButton: "Generate PDF"
  },
  navigation: {
    start: "Start",
    next: "Next",
    previous: "Previous"
  },
  loading: {
    generatingResults: "Generating your results"
  },
  admin: {
    login: {
      title: "Admin Panel",
      subtitle: "Risk Profile Management",
      emailPlaceholder: "Email",
      passwordPlaceholder: "Password",
      loginButton: "Sign In",
      invalidCredentials: "Invalid username or password"
    },
    dashboard: {
      title: "Risk Profile History",
      subtitle: "View and manage all generated risk profiles",
      logoutButton: "Sign Out",
      searchPlaceholder: "Search by client or advisor name...",
      filterByProfile: "Filter by profile",
      filterByLanguage: "Filter by language",
      allProfiles: "All Profiles",
      allLanguages: "All Languages",
      tableHeaders: {
        date: "Date",
        client: "Client",
        advisor: "Advisor",
        score: "Score",
        profile: "Profile",
        language: "Language",
        actions: "Actions"
      },
      actions: {
        view: "View",
        download: "Download",
        delete: "Delete"
      },
      noResults: "No risk profiles found",
      noResultsDescription: "Try adjusting your search or filters",
      totalResults: "Total: {count} profiles",
      confirmDelete: "Are you sure you want to delete this risk profile?",
      deleteSuccess: "Risk profile deleted successfully",
      deleteCancel: "Cancel"
    }
  }
};

export type FundItem = { name: string; composition: string };
type _En = typeof en;

// Explicit type for risk profile entries that allows any string keys for funds and ranges
// This avoids TypeScript's excess property check issues with localized keys in es.ts and pt.ts
export type TranslationType = Omit<_En, 'riskProfiles'> & {
  riskProfiles: {
    [key: string]: {
      profile: string;
      description: string;
      scoreRange: string;
      ranges: { category: string; min: number; max: number; color: string }[];
      funds: { [category: string]: FundItem[] };
    };
  };
};